import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../utils/api';
import { useAuthContext } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { sanitizePhone, normalizeSwedishPhone, swedishMobileError } from '../utils/inputSanitizers';

/**
 * CompleteSignup - Completes Google OAuth registration
 * Allows user to choose role (Payer/Buddy) and add Swish number
 */
export function CompleteSignup() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const authContext = useAuthContext();

  const calculateAge = (dateStr) => {
    if (!dateStr) return null;
    const birth = new Date(dateStr);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
      age -= 1;
    }
    return age;
  };
  
  const [role, setRole] = useState('payer');
  const [phoneError, setPhoneError] = useState('');
  const [phone, setPhone] = useState('');
  const [birthdate, setBirthdate] = useState('');
  const [loading, setLoading] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  // Get user info from location state first (passed from AuthCallback), then auth context
  useEffect(() => {
    const userFromState = location.state?.user;
    const userFromContext = authContext?.user;
    
    if (userFromState) {
      setCurrentUser(userFromState);
    } else if (userFromContext) {
      // If user already has Swish, redirect to dashboard
      if (userFromContext.swish_payee_alias) {
        navigate('/dashboard', { replace: true });
        return;
      }
      setCurrentUser(userFromContext);
    } else {
      // No user data - check localStorage
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        if (parsedUser.swish_payee_alias) {
          navigate('/dashboard', { replace: true });
          return;
        }
        setCurrentUser(parsedUser);
      } else {
        // No user at all, redirect to login
        navigate('/', { replace: true });
      }
    }
  }, [location.state?.user, authContext?.user, navigate]);

  const handleComplete = async () => {
    const phoneErr = swedishMobileError(phone);
    if (phoneErr) { setPhoneError(phoneErr); return; }

    if (role === 'buddy' && !birthdate) {
      toast.error('Birthdate is required for Minors/Buddys');
      return;
    }

    setLoading(true);
    try {
      // Get token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Session expired. Please login again.');
        navigate('/', { replace: true });
        return;
      }
      
      const headers = { Authorization: `Bearer ${token}` };
      const phoneNorm = normalizeSwedishPhone(phone) || null;
      // Personal (payer) Swish is always the mobile number.
      const swishNorm = role === 'payer' ? phoneNorm : null;
      
      // Update user profile with role and Swish
      await axios.put(`${API}/payer/profile`, {
        role: role === 'payer' ? 'parent' : 'mobpay',
        swish_payee_alias: swishNorm,
        phone: phoneNorm,
        birthdate: birthdate || null
      }, { headers });

      // Update local user data
      const ageValue = calculateAge(birthdate);
      const updatedUser = {
        ...currentUser,
        role: role === 'payer' ? 'parent' : 'mobpay',
        user_type: role === 'payer' ? 'PAYER' : 'BUDDY',
        swish_payee_alias: swishNorm,
        phone: phoneNorm,
        birthdate: birthdate || null,
        buddy_type: role === 'buddy' ? (ageValue !== null && ageValue < 18 ? 'minor' : 'buddy') : currentUser.buddy_type
      };
      
      // Update localStorage and context
      localStorage.setItem('user', JSON.stringify(updatedUser));
      if (authContext?.save) {
        authContext.save(token, updatedUser);
      }

      toast.success('Profile completed successfully!');
      navigate('/dashboard', { replace: true });
    } catch (e) {
      console.error('Profile completion error:', e);
      toast.error(e.response?.data?.detail || 'Failed to complete profile');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRegistration = async () => {
    setCanceling(true);
    let cancelled = false;
    try {
      const token = localStorage.getItem('token');
      if (token) {
        // Legacy opaque-session provisional account cleanup (best-effort).
        try {
          await axios.post(`${API}/auth/google/cancel`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } catch (_) { /* noop */ }
      }
      // Drop any live Supabase (social) session so the user really starts over.
      try { if (supabase) await supabase.auth.signOut(); } catch (_) { /* noop */ }
      cancelled = true;
    } catch (e) {
      console.error('Cancel registration error:', e);
      toast.error(e.response?.data?.detail || 'Failed to cancel registration');
    } finally {
      if (authContext?.clear) {
        authContext.clear();
      } else {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
      if (cancelled) {
        toast.success('Registration cancelled');
      }
      navigate('/', { replace: true });
      setCanceling(false);
    }
  };

  // Show loading while checking for user
  if (!currentUser) {
    return (
      <div className="min-h-screen bg-green-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto mb-2"></div>
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-green-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center mb-4">
            {currentUser.profile_icon ? (
              <img 
                src={currentUser.profile_icon} 
                alt={currentUser.full_name} 
                className="w-14 h-14 rounded-full object-cover"
              />
            ) : (
              <span className="text-2xl font-bold text-teal-600">
                {currentUser.full_name?.charAt(0) || '?'}
              </span>
            )}
          </div>
          <CardTitle className="text-2xl">Welcome, {currentUser.full_name}!</CardTitle>
          <CardDescription>
            Complete your profile to start using PayMig
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Role Selection */}
          <div className="space-y-2">
            <Label>I want to be a</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="role-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="payer">
                  <div className="flex flex-col">
                    <span className="font-medium">Payer</span>
                    <span className="text-xs text-muted-foreground">Send and receive payments via Swish</span>
                  </div>
                </SelectItem>
                <SelectItem value="buddy">
                  <div className="flex flex-col">
                    <span className="font-medium">Buddy</span>
                    <span className="text-xs text-muted-foreground">Family member managed by a Payer</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Phone Number */}
          <div className="space-y-2">
            <Label htmlFor="phone">Phone Number <span className="text-red-500">*</span></Label>
            <Input
              id="phone"
              type="tel"
              placeholder="e.g., 070-123 45 67"
              value={phone}
              onChange={(e) => { setPhone(sanitizePhone(e.target.value)); setPhoneError(''); }}
              inputMode="tel"
              aria-invalid={!!phoneError}
              className={phoneError ? 'border-red-500' : ''}
              data-testid="phone-input-complete-signup"
            />
            {phoneError && <p className="text-xs text-red-600" data-testid="phone-error-complete-signup">{phoneError}</p>}
          </div>

          {role === 'buddy' && (
            <div className="space-y-2">
              <Label htmlFor="birthdate">Birthdate</Label>
              <Input
                id="birthdate"
                type="date"
                value={birthdate}
                onChange={(e) => setBirthdate(e.target.value)}
                data-testid="signup-birthdate-input"
              />
            </div>
          )}

          {/* Swish Number - Required for Payers */}
          {role === 'payer' && (
            <div className="space-y-2">
              <Label htmlFor="swish">
                Swish Number <span className="text-red-500">*</span>
              </Label>
              <Input
                id="swish"
                type="text"
                placeholder="Same as your mobile number"
                value={normalizeSwedishPhone(phone) || ''}
                readOnly
                disabled
                inputMode="text"
                data-testid="swish-input-complete-signup"
              />
              <p className="text-xs text-muted-foreground">
                Your Swish number is your mobile number
              </p>
            </div>
          )}

          {/* Info for Buddy */}
          {role === 'buddy' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                As a Buddy, you'll need to be connected to a Payer (parent/guardian) 
                who will manage your account and approve transactions.
              </p>
            </div>
          )}

          {/* Submit Button */}
          <Button 
            className="w-full" 
            onClick={handleComplete}
            disabled={loading}
            data-testid="complete-signup-btn"
          >
            {loading ? 'Saving...' : 'Complete Setup'}
          </Button>

          {/* Skip for now - only for Buddy */}
          {role === 'buddy' && (
            <Button 
              variant="ghost" 
              className="w-full"
              onClick={() => navigate('/dashboard', { replace: true })}
              data-testid="skip-complete-signup-btn"
            >
              Skip for now
            </Button>
          )}

          {/* Cancel Registration */}
          <Button
            variant="outline"
            className="w-full"
            onClick={handleCancelRegistration}
            disabled={canceling}
            data-testid="cancel-google-signup-btn"
          >
            {canceling ? 'Cancelling...' : 'Cancel Registration'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
